import { requestApi } from 'serviceWorker/api/requestApi';
import * as dbIssues from 'serviceWorker/db/issues/issues';
import { captureSelectedProjectScope } from 'serviceWorker/helpers/dataScope';
import { EntityOperation, logEntityOperationFailure } from 'serviceWorker/helpers/entityFailureLog';
import { isNotFoundOrForbidden } from 'serviceWorker/services/httpQueue/errorHandlers/helpers';
import type { IssueInDto } from 'shared/dtos/in/issue';

/** Reads of one issue running now, by id (an issue belongs to one project): concurrent reads share one request. */
const runningFetches = new Map<string, Promise<void>>();

async function fetchAndStoreIssue(issueId: string): Promise<void> {
  // The project the read was made in: the request and the write both belong to it.
  const scope = await captureSelectedProjectScope();
  const { projectId } = scope;
  if (!projectId) {
    return;
  }
  try {
    const issueInDto = await requestApi<IssueInDto>({ method: 'GET', path: `project/${projectId}/issue/${issueId}` });
    await scope.write(() => dbIssues.addBatch([issueInDto]));
  } catch (error) {
    // Not returned to this user (it does not exist, or no access): it stays unknown. Other failures are logged.
    if (!isNotFoundOrForbidden(error)) {
      logEntityOperationFailure(EntityOperation.FETCHING, { entityName: 'issues', projectId, error });
    }
  }
}

/**
 * Fetches one issue the stored collection lacks (created since the last sync,
 * deleted, opened from a link before it was downloaded) and stores it, instead
 * of downloading every issue again. Never rejects: what the server does not
 * return, or an API that is not reached, leaves the issue unknown. The server
 * answers a deleted issue too, with its `deleted` flag.
 */
export function fetchIssueById(issueId: string): Promise<void> {
  const running = runningFetches.get(issueId);
  if (running) {
    return running;
  }
  const fetching = fetchAndStoreIssue(issueId).finally(() => runningFetches.delete(issueId));
  runningFetches.set(issueId, fetching);
  return fetching;
}
